import React, { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { useStoryblokState } from 'gatsby-source-storyblok';
import PropTypes from 'prop-types';
import { SCROLL_DOWN, SCROLL_UP } from '../constants';
import { useBoolean, useIsomorphicLayoutEffect } from '../hooks';
import { buildInitialSettings, settingsReducer } from '../state/settings/settingsReducer';

/**
 * @type {React.Context<AppState>}
 */
const AppStateContext = React.createContext({});

/**
 * @typedef {Object} AppStateSettings
 * @property {boolean} navFontColorLight
 * @property {boolean} navLightMode
 * @property {boolean} showCartIcon
 * @property {boolean} showSearchIcon
 */

/**
 * @typedef {Object} AppState
 * @property {AppStateSettings} settings
 * @property {({ name: string, value: any }) => void} dispatchSetting
 * @property {any[]} primaryNavLinks
 * @property {(value: any[]) => void} setPrimaryNavLinks
 * @property {any[]} popularSearches
 * @property {(value: any[]) => void} setPopularSearches
 * @property {*} footerBlok
 * @property {(value: any) => void} setFooterBlok
 * @property {boolean} searchIsOpen
 * @property {() => void} toggleSearch
 * @property {boolean} hamburgerMenuIsOpen
 * @property {() => void} toggleHamburgerMenu
 * @property {number} headerHeight
 * @property {(number) => void} setHeaderHeight
 * @property {number} headerTranslateY
 * @property {(number) => void} setHeaderTranslateY
 */

/**
 * @returns {AppState}
 */
export const useAppState = () => {
  /**
   * @type {AppState}
   */
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error('useAppState must be used within the AppStateProvider');
  }
  return context;
};

const AppStateProvider = ({ children, blok }) => {
  const [settings, dispatchSetting] = useReducer(settingsReducer, blok, buildInitialSettings);

  // Header/Footer/Menu/SearchSuggestions all read primaryNavLinks/popularSearches from here,
  // but the header story itself is only available where HeaderWrapper renders (a sibling, not
  // an ancestor, of Footer/Menu/Search) — so this Provider queries it too (same cheap,
  // build-time-resolved static query Gatsby already dedupes) purely to seed a correct
  // synchronous initial value. Header.jsx's own existing effect (fed by the same
  // editor-bridge-reactive story data) still handles keeping these in sync afterward.
  const headerData = useStaticQuery(graphql`
    query AppStateHeaderStoryQuery {
      storyblokEntry(full_slug: { eq: "header" }) {
        content
      }
    }
  `);
  const headerStory = useStoryblokState(headerData.storyblokEntry);

  const [primaryNavLinks, setPrimaryNavLinks] = useState(() => headerStory?.content?.links ?? []);
  const [popularSearches, setPopularSearches] = useState(() => headerStory?.content?.popularSearches ?? []);
  const [footerBlok, setFooterBlok] = useState(null);
  const { value: searchIsOpen, toggle: toggleSearch, setFalse: closeSearch } = useBoolean(false);
  const { value: hamburgerMenuIsOpen, toggle: toggleHamburgerMenu } = useBoolean(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [headerTranslateY, setHeaderTranslateY] = useState(0);

  useEffect(() => {
    if (searchIsOpen) {
      setTimeout(() => {
        document.documentElement.classList.add('is-locked');
      }, 300);
    } else {
      setTimeout(() => {
        document.documentElement.classList.remove('is-locked');
      }, 300);
    }
  }, [searchIsOpen]);

  // Search closed on outside click
  useEffect(() => {
    const handleOutsideSearchClick = (event) => {
      if (searchIsOpen && event.target.id === 'outerSearchEl') {
        closeSearch();
      }
    };

    document.addEventListener('click', handleOutsideSearchClick);

    // Unbind the event listener on clean up
    return () => {
      document.removeEventListener('click', handleOutsideSearchClick);
    };
  }, [searchIsOpen, closeSearch]);

  useEffect(() => {
    if (searchIsOpen) {
      setTimeout(() => {
        document.documentElement.classList.add('is-locked');
      }, 300);
    } else {
      setTimeout(() => {
        document.documentElement.classList.remove('is-locked');
      }, 300);
    }
  }, [searchIsOpen]);

  const lastScrollRef = useRef(0);
  const lastPositionRef = useRef(0);
  const tickingRef = useRef(false);
  const handleDynamicHeader = useCallback(() => {
    const { body } = document;
    const currentScroll = window.scrollY;
    if (currentScroll <= 0) {
      body.classList.remove(SCROLL_UP);
      setHeaderTranslateY(0);
      return;
    }
    if (currentScroll > lastScrollRef.current && !body.classList.contains(SCROLL_DOWN)) {
      // down
      body.classList.remove(SCROLL_UP);
      body.classList.add(SCROLL_DOWN);
      lastPositionRef.current = currentScroll;
    } else if (currentScroll < lastScrollRef.current && body.classList.contains(SCROLL_DOWN)) {
      // up
      body.classList.remove(SCROLL_DOWN);
      body.classList.add(SCROLL_UP);
      lastPositionRef.current = currentScroll;
    }

    lastScrollRef.current = currentScroll;
    let scrollDeltaY;
    if (body.classList.contains(SCROLL_DOWN)) {
      scrollDeltaY = lastScrollRef.current - lastPositionRef.current;
      if (scrollDeltaY > headerHeight) {
        scrollDeltaY = headerHeight;
      }
    } else {
      scrollDeltaY = lastPositionRef.current - lastScrollRef.current;
      if (scrollDeltaY > headerHeight) {
        scrollDeltaY = headerHeight;
      }

      scrollDeltaY = headerHeight - scrollDeltaY;
    }
    setHeaderTranslateY(scrollDeltaY / headerHeight);
  }, [headerHeight]);

  useIsomorphicLayoutEffect(() => {
    const onScroll = () => {
      if (tickingRef.current) return;
      tickingRef.current = true;
      requestAnimationFrame(() => {
        handleDynamicHeader();
        tickingRef.current = false;
      });
    };

    handleDynamicHeader();

    window.addEventListener('scroll', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [headerHeight, handleDynamicHeader]);

  const contextValue = useMemo(
    () => ({
      settings,
      dispatchSetting,
      primaryNavLinks,
      setPrimaryNavLinks,
      popularSearches,
      setPopularSearches,
      footerBlok,
      setFooterBlok,
      searchIsOpen,
      toggleSearch,
      hamburgerMenuIsOpen,
      toggleHamburgerMenu,
      headerHeight,
      setHeaderHeight,
      headerTranslateY,
      setHeaderTranslateY,
    }),
    [
      settings,
      primaryNavLinks,
      popularSearches,
      footerBlok,
      searchIsOpen,
      toggleSearch,
      hamburgerMenuIsOpen,
      toggleHamburgerMenu,
      headerHeight,
      headerTranslateY,
    ],
  );

  return <AppStateContext.Provider value={contextValue}>{children}</AppStateContext.Provider>;
};

AppStateProvider.propTypes = {
  children: PropTypes.any,
  blok: PropTypes.object,
};

AppStateProvider.defaultProps = {
  blok: {},
};

export default AppStateProvider;
